/**
 * Shared nav-route resolution — extracted from `SidebarNav.tsx` (Icarus
 * D&D redesign) so the new `Topbar` can reuse the exact same
 * access-control / visibility logic instead of duplicating it. Behaviour
 * is unchanged; this is a pure extraction.
 *
 * `filterByAccess` enforces what the route registry already declares
 * (feature flags, role, org setting-type scope). `partitionRoutesByVisibility`
 * then splits the accessible set into primary / secondary / hidden based
 * on org-configurable `nav_overrides`, and superadmins always see every
 * secondary entry regardless of per-org overrides.
 */

import { useMemo } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatures } from '@/hooks/useFeatures';
import { useEffectiveRole } from '@/hooks/useEffectiveRole';
import { isEnabled } from '@/config/feature-flags';
import { ROUTES } from '@/features/route-registry';
import { profilesRepo } from '@/data';
import { getErrorMessage } from '@/lib/errors';
import {
  partitionRoutesByVisibility,
  type OrgSettingType,
} from '@/features/visibility';
import { applyNavTabOrder, parseNavTabOrder } from '@/features/navTabOrder';
import type { Role, RouteModule } from '@/features/types';
import type { Feature } from '@/hooks/useFeatures';

function compareNavOrder(a: RouteModule, b: RouteModule): number {
  const ao = a.nav?.order ?? Number.MAX_SAFE_INTEGER;
  const bo = b.nav?.order ?? Number.MAX_SAFE_INTEGER;
  return ao - bo;
}

function filterByAccess(
  routes: readonly RouteModule[],
  opts: {
    section: 'main' | 'admin';
    role: Role | undefined;
    hasFeature: (feature: Feature) => boolean;
    settingType: OrgSettingType;
  },
): RouteModule[] {
  return routes
    .filter((route) => {
      if (!route.nav) return false;
      if (route.featureFlag && !isEnabled(route.featureFlag)) return false;
      const sec = route.nav.section ?? 'main';
      if (sec !== opts.section) return false;
      if (route.feature && !opts.hasFeature(route.feature)) return false;
      if (route.roles && route.roles.length > 0) {
        if (!opts.role || !route.roles.includes(opts.role)) return false;
      }
      if (
        route.scope &&
        route.scope !== 'both' &&
        route.scope !== opts.settingType
      ) {
        return false;
      }
      return true;
    })
    .slice()
    .sort(compareNavOrder);
}

export function isRouteActive(route: RouteModule, pathname: string): boolean {
  const matchStartsWith =
    route.nav?.matchStartsWith ?? !route.path.includes(':');
  if (matchStartsWith) {
    if (route.path === '/') return pathname === '/';
    return pathname === route.path || pathname.startsWith(`${route.path}/`);
  }
  return pathname === route.path;
}

/** Admin-section routes (footer/settings family) — access-filtered, unsplit. */
export function useAdminNavRoutes(): RouteModule[] {
  const { hasFeature, settingType } = useFeatures();
  const effectiveRole = useEffectiveRole();
  return useMemo(
    () =>
      filterByAccess(ROUTES, {
        section: 'admin',
        role: effectiveRole,
        hasFeature,
        settingType,
      }),
    [hasFeature, effectiveRole, settingType],
  );
}

/** Main-section routes, split into primary / secondary per org nav_overrides. */
export function useMainNavRoutes(): {
  primary: RouteModule[];
  secondary: RouteModule[];
  /**
   * Persistiert eine neue Reihenfolge der primären Tabs (Bookmark-
   * Leisten-Muster) — `nextIds` ist die vollständige gewünschte
   * Reihenfolge der aktuell sichtbaren primären Route-IDs. Optimistisches
   * Update + Best-effort-Schreibvorgang; No-op ohne eingeloggten Nutzer
   * (z.B. Demo-Modus).
   */
  reorderPrimary: (nextIds: string[]) => void;
} {
  const { profile, updateLocalProfile } = useAuth();
  const { hasFeature, navOverrides, settingType } = useFeatures();
  const effectiveRole = useEffectiveRole();
  const isSuperadmin = Boolean(profile?.is_superadmin);

  const accessible = useMemo(
    () =>
      filterByAccess(ROUTES, {
        section: 'main',
        role: effectiveRole,
        hasFeature,
        settingType,
      }),
    [hasFeature, effectiveRole, settingType],
  );

  const tabOrder = parseNavTabOrder(profile?.nav_tab_order);

  const { primary, secondary } = useMemo(() => {
    const { primary, secondary, hidden } = partitionRoutesByVisibility(
      accessible,
      navOverrides,
    );
    return {
      // Persönliche Reihenfolge (Bookmark-Leisten-Muster) gilt nur für die
      // primäre Reihe, nicht für "Mehr…" — siehe `src/features/navTabOrder.ts`.
      primary: applyNavTabOrder(primary, tabOrder),
      secondary: isSuperadmin ? [...secondary, ...hidden] : secondary,
    };
  }, [accessible, navOverrides, isSuperadmin, tabOrder]);

  const reorderPrimary = (nextIds: string[]) => {
    const userId = profile?.user_id;
    if (!userId) return;
    updateLocalProfile({ nav_tab_order: nextIds });
    profilesRepo.saveNavTabOrder(userId, nextIds).catch((err) => {
      toast.error('Reihenfolge konnte nicht gespeichert werden.', {
        description: getErrorMessage(err),
      });
    });
  };

  return { primary, secondary, reorderPrimary };
}
